import React from 'react';
import clsx from 'clsx';
import { EvidenceCitation } from '@api/assistant/assistantApi';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgVideo from '@icons/video.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgExternalLink from '@icons/external-link.svg?react';

interface ComponentProps {
  citation: EvidenceCitation;
}

/** Titles arrive as source filenames, e.g. `10 Timesloty zaklad_zakaznici.md`. */
const citationTitle = (citation: EvidenceCitation): string => {
  if (!citation.title) return citation.source_uri || citation.doc_id;
  return citation.title
    .replace(/\.md$/i, '')
    .replace(/^\d+[A-Z]?\s+/, '')
    .replace(/_/g, ' ')
    .trim();
};

/**
 * The API does not type its sources, so a video is recognised by where it points — or, for sources
 * without a link, by the `> Video link:` line the video guides open with.
 */
const isVideo = (citation: EvidenceCitation): boolean =>
  /youtube\.com|youtu\.be|vimeo\.com|loom\.com|\.(mp4|webm|mov)(\?|$)/i.test(citation.source_uri ?? '') || /video link:/i.test(citation.snippet ?? '');

/**
 * One related guide under an answer (Figma 21746:18318): a type badge, the guide's title and kind, and —
 * when the source can be opened — an external-link mark, the whole row linking out in a new tab.
 *
 * Rows are joined into one card by AssistantMessageBubble (1px gaps, outer corners rounded), so a row
 * has no radius of its own beyond the first/last.
 */
const AssistantCitationCard: React.FC<ComponentProps> = ({ citation }) => {
  const { tr } = useTranslationLgs();
  const video = isVideo(citation);
  const href = citation.source_uri || undefined;
  const Tag = href ? 'a' : 'div';

  return (
    <Tag
      {...(href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={clsx(
        'group flex w-full items-center gap-2 bg-white px-4 py-2 no-underline shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] first:rounded-t-lg last:rounded-b-lg',
        href && 'cursor-pointer',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-content-brandMedium text-content-brand">
          {video ? <SvgVideo className="h-3 w-3" /> : <SvgFile className="h-3 w-3" />}
        </div>
        <div className="flex min-w-0 flex-col justify-center font-medium">
          <div className="truncate text-sm tracking-[-0.14px] text-content-primary">{citationTitle(citation)}</div>
          <div className="text-xs tracking-[-0.12px] text-content-secondary">
            {video ? tr('Assistant.citationVideo', 'Video') : tr('Assistant.citationDocument', 'Dokument')}
          </div>
        </div>
      </div>
      {href && <SvgExternalLink className="h-4 w-4 shrink-0 text-content-brand transition-all duration-200 group-hover:text-complementary-brandDark" />}
    </Tag>
  );
};

export default AssistantCitationCard;
